// Thin adapter over the shared engine. The app imports engine features ONLY from here, so a
// module that is still being written can be swapped in without touching call sites.
//
// Status (see ARCHITECTURE.md → "Engine adapter"): every engine module the app uses exists and is
// re-exported here: course, normalize, paradigm, phonology, labels, dictionary (search), analyser,
// translator, validator, exercises. paradigmFor is wrapped (lib/paradigm.ts) so a throw on odd data
// degrades to the stored override forms (no seam) instead of breaking a study session.
//
// Start-up size: this file is in the start-up chunk (course and time helpers), so it only
// re-exports. The engine package is marked "sideEffects": false, which lets the bundler leave a
// re-exported module (translator, validator, exercises, the paradigm generator and its templates)
// out of the start-up chunk and put it in the chunks of the screens that use it. Do not add code
// here that calls into a heavy module at the top level or in a function defined here.

import { MACRON_KEYS as ENGINE_MACRON_KEYS } from '@valyrio/engine/normalize';

export {
  localDate,
  startOfLocalDay,
  addDays,
  daysBetween,
  dayOfWeek,
  isoWeek,
  lessonsUnlocked,
  nextUnlock,
  newPerDay,
  courseDay,
  unlockDateOf,
  lessonsOpen,
  lessonsScheduled,
  passedThrough,
  passedByAll,
  scheduleStatus,
  releaseTarget,
  LESSON_WEEKDAY,
  type ScheduleStatus,
  courseWordList,
  releasedWords,
  wordsAvailableForUser,
  type CourseWord,
  // opening the next lesson early by a joint vote (2026-10-07)
  earlyOpenOffer,
  lessonWeekday,
  type EarlyOpenOffer,
  // the rising word pace (2026-10-14): the step table and the next rise
  paceSteps,
  nextRateRise,
  type PaceStep,
  type RateRise,
} from '@valyrio/engine/course';
export { normalizeHv, foldForSearch, stripMacrons, hvLetters, type NormalizeOptions } from '@valyrio/engine/normalize';
export { formKey, parseFormKey as parseEngineFormKey } from '@valyrio/engine/paradigm';
export { ipa, stressDisplay, stressDisplayText, type StressDisplay } from '@valyrio/engine/phonology';
export { borrowedStressDisplay } from '@valyrio/engine/borrowed-stress';
export { paradigmNotesFor } from '@valyrio/engine/rules/paradigm-notes';
export { tagLabel, tagAbbr, formLabel as tagsLabel, formAbbr as tagsAbbr, formsSummary, fieldValues, FIELD_LABELS } from '@valyrio/engine/labels';
/** tagsLabel's parts and the "yksikön 1. persoona" phrase (a lesson's <Paradigm filter> list, lessons/components/paradigmList.ts). */
export { formLabelParts as tagsLabelParts, personNumberLabel, type LabelPart } from '@valyrio/engine/labels';
/** Translations after High Valyrian examples in the lessons and on the grammar page. */
export { glossKey, inlineLabel, isAffixText, shortMeaning, wordCount } from '@valyrio/engine/inline-gloss';
export { glossPinKey, inlineSentence, splitFormPin } from '@valyrio/engine/inline-gloss';

// Dictionary search, analyser, translator, validator, exercises (engine, all live).
export { Dictionary as EngineDictionary, glossHeads, primaryHead, type SearchHit, type SearchMatch, type GlossView } from '@valyrio/engine/dictionary';
export { bundleFromSearchPreview, setSearchRanking, type SearchOptions, type SearchPreview } from '@valyrio/engine/search';
export { CompactFormIndex, isFormIndexSnapshot, type FormIndexSnapshot } from '@valyrio/engine/formIndexCompact';
/** Form index validity: bundle version + the engine's paradigm version (engine/src/version.ts). */
export { ENGINE_FORMS_VERSION, formIndexStamp } from '@valyrio/engine/version';
export {
  MapFormIndex,
  buildFormIndexAsync,
  tokenizeHv,
  type HvReading,
  type HvToken,
  type SerializedFormIndex,
  type FormIndex,
} from '@valyrio/engine/analyser';
export {
  translate,
  analyseHvSentence,
  hvTokenColumns,
  prewarmTranslator,
  outputSenses,
  type ForcedReading,
  type PrewarmStatus,
} from '@valyrio/engine/translator';
/** Self-check of an HV text (forms, agreement, governed cases, round trip): Lausepino's fix form. */
export { checkHvOutput } from '@valyrio/engine/self-check';
// Analogy to attested sentences and partial matches from the users' approved translations
// (translator phase 3 items 8, 9; screen-only, never in translate()).
export { findAnalogies, officialFrames, type AnalogyFrame, type AnalogyResult, type AnalogySwap } from '@valyrio/engine/analogy';
export { approvedFrames, partialFromMemory, type MemoryPartial, type MemoryPart } from '@valyrio/engine/memory-pieces';
// Verified memory first, the miss queue, ask instead of guess (Axel 2026-10-09; screen-only, never in translate()).
export {
  findNear,
  halloweenEntries,
  memoryResult,
  sessionEntries,
  translationOf,
  sourceSideOf,
  type NearHit,
  type NearPart,
  type VerifiedEntry,
  type VerifiedResult,
} from '@valyrio/engine/verified-memory';
export { askReading, type ReadingOption, type ReadingQuestion, type ReadingPin } from '@valyrio/engine/ask-reading';
export {
  missReasons,
  missKey,
  newMissId,
  missWaiting,
  type MissReason,
  type MissReading,
  type NewTranslatorMiss,
  type TranslatorMiss,
} from '@valyrio/engine/miss-queue';
// Translator screen tools (phase 3 items 13–15, phase 4 item 13).
export { swapCandidates, sourceWordBases, type SwapCandidate } from '@valyrio/engine/lemma-swap';
export { backTranslate, type BackTranslation, type BackMismatch } from '@valyrio/engine/back-translation';
export { detectLanguage, detectLookups, type DetectedLang, type Detection, type DetectLookups } from '@valyrio/engine/detect-language';
export { describeAlternative, type AlternativeDiff, type AltWord } from '@valyrio/engine/alternatives';
export { missingWordHelp, type MissingWordHelp, type NearWord, type Paraphrase } from '@valyrio/engine/missing-word';
// Marking a translation's mistakes (translator phase 5 items 11–12): translator/marks.ts, MarkPanel.tsx.
export { alignSource, hintsFromAnnotation, markContext, outputTokens, sourceTokens as markSourceTokens, type MarkPins } from '@valyrio/engine/annotation';
// Letters, borrowed names and the register (translator phase 3 items 16–17, phase 4 items 17–18).
export { translateLetter, type LetterResult, type LetterLine, type LetterLineKind } from '@valyrio/engine/letter';
export { borrowedName, isBorrowedNameId, suggestNounClass, suggestedParadigm, type BorrowedName, type ClassSuggestion } from '@valyrio/engine/names';
export { FORMAL_TITLES, type LetterTone, type LetterRegister } from '@valyrio/engine/rules/letter';
/** "Which case and why" texts (fi/en per case), used by exercises and the translator's Selitä. */
export { CASE_WHY } from '@valyrio/engine/rules/cases';
export { validateOwnWord, inferClass, type OwnWordResult, type ValidationIssue, type InferredClass, type Clash } from '@valyrio/engine/validator';
export {
  buildExercises,
  checkAnswer as checkExercise,
  sceneTag,
  type Exercise,
  type ExerciseKind,
  type CheckResult as ExerciseCheck,
  type ExerciseSource,
  type TranslateExercise,
  type FillExercise,
  type OrderExercise,
  type CaseExercise,
  askLabel,
  matchesFocus,
  caseContext,
  type CheckFocus,
  type CheckOptions,
} from '@valyrio/engine/exercises';
// Listening (Kuuntele ja käännä): app/src/listen/.
export {
  listenSources,
  sourceAudio,
  sourceTokens,
  introducedShare,
  LISTEN_GATE,
  SHORT_CLIP_SEC,
  type ListenExercise,
  type ListenMode,
} from '@valyrio/engine/exercises';

/** The on-screen macron row, lowercase (Shift capitalises). */
export const MACRON_KEYS: readonly string[] = ENGINE_MACRON_KEYS.map((k) => k.key);

// ---------- paradigm ----------

export { paradigmFor } from './paradigm';

/** Which engine modules are live vs fallback (tests read this; no adapter fallbacks remain). */
export const engineStatus = {
  normalize: 'engine',
  paradigm: 'engine',
  course: 'engine',
  phonology: 'engine',
  labels: 'engine',
  dictionary: 'engine',
  analyser: 'engine',
  translator: 'engine',
  validator: 'engine',
  exercises: 'engine',
} as const;
